import { memo, useEffect, useState } from 'react';
import { Button, Dialog } from '@effizency/efz-design-system';
import { useNavigate } from 'react-router-dom';

import CookieConsent from 'components/CookieConsent';
import IntlMessages from 'components/util/IntlMessages';

import useCookies from 'hooks/cookies';

import { routeToCookiePolicy } from 'services/cookies';

import { useUserStore } from 'stores/user';

import type { TCookiesType } from 'types/cookies';

interface ICustomizeCookiesDialogProps {
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
    cookieBannerHandler?: {
        accept: () => void;
    };
}

const CustomizeCookiesDialog = ({ isOpen, setIsOpen, cookieBannerHandler }: ICustomizeCookiesDialogProps) => {
    const navigate = useNavigate();

    const { setCookie, PLATFORM_CUSTOMIZATION_COOKIE, PERSONALIZED_ADVERTISING_COOKIE } = useCookies();

    const {
        auth: { isUserAuthenticated },
    } = useUserStore((state) => state.user);

    const [showPolicy, setShowPolicy] = useState(false);
    const [policyTypeToShow, setPolicyTypeToShow] = useState<TCookiesType | null>(null);

    const showPolicyHandler = (type: TCookiesType | null = null) => {
        if (type) {
            if (!isUserAuthenticated) {
                setPolicyTypeToShow(type);
            } else {
                setIsOpen(false);
                routeToCookiePolicy(navigate, type);
            }
        } else {
            setShowPolicy(false);
        }
    };

    useEffect(() => {
        if (policyTypeToShow) {
            setShowPolicy(true);
        } else {
            setShowPolicy(false);
        }
    }, [policyTypeToShow]);

    useEffect(() => {
        if (!showPolicy) {
            setPolicyTypeToShow(null);
        }
    }, [showPolicy]);

    const switchHandler = () => {
        setCookie(PLATFORM_CUSTOMIZATION_COOKIE, true, 365);
        setCookie(PERSONALIZED_ADVERTISING_COOKIE, true, 730);
        setIsOpen(false);
        cookieBannerHandler?.accept();
    };

    return (
        <>
            {!showPolicy && (
                <Dialog
                    open={isOpen}
                    onClose={() => setIsOpen(false)}
                    title={<IntlMessages id="label.cookies.privacySettings" />}
                    maxWidth="md"
                    dataTestId="customize-cookies-dialog"
                    primaryButton={
                        <Button type="button" onClick={switchHandler} dataTestId="customize-cookies-consent">
                            <IntlMessages id="label.consent" />
                        </Button>
                    }
                >
                    <section className="privacy-section">
                        <div className="privacy-section-content">
                            <div>
                                <IntlMessages id="dialog.privacy.1" />
                                <br />
                                <ul>
                                    <li>
                                        <IntlMessages id="dialog.privacy.1.1" />
                                    </li>
                                    <li>
                                        <IntlMessages id="dialog.privacy.1.2" />
                                    </li>
                                    <li>
                                        <IntlMessages id="dialog.privacy.1.3" />
                                    </li>
                                    <li>
                                        <IntlMessages id="dialog.privacy.1.4" />
                                    </li>
                                    <li>
                                        <IntlMessages id="dialog.privacy.1.5" />
                                    </li>
                                </ul>
                                <IntlMessages id="dialog.privacy.2.1" whitespaceEnd />
                                <span className="link" onClick={() => showPolicyHandler('privacy-policy')}>
                                    <IntlMessages id="label.here" />
                                </span>
                                <IntlMessages id="dialog.privacy.2.2" whitespaceEnd />
                                <span className="link" onClick={() => showPolicyHandler('cookies-policy')}>
                                    <IntlMessages id="label.here" />
                                </span>
                                .
                            </div>
                            <div className="label-category" />
                        </div>
                    </section>

                    <section className="privacy-section">
                        <div className="privacy-section-title">
                            <div className="privacy-section-title-first-line">
                                <span>
                                    <IntlMessages id="dialog.privacy.essential" />
                                </span>
                                <span className="label-category">
                                    <IntlMessages id="label.alwaysOn" />
                                </span>
                            </div>
                            <hr />
                        </div>
                        <div className="privacy-section-content">
                            <div>
                                <IntlMessages id="dialog.privacy.essential.1" />
                            </div>
                            <div className="label-category" />
                        </div>
                    </section>

                    <section className="privacy-section">
                        <div className="privacy-section-title">
                            <div className="privacy-section-title-first-line">
                                <span>
                                    <IntlMessages id="dialog.privacy.customization" />
                                </span>
                                <span className="label-category">
                                    <IntlMessages id="label.alwaysOn" />
                                </span>
                            </div>
                            <hr />
                        </div>
                        <div className="privacy-section-content">
                            <div>
                                <IntlMessages id="dialog.privacy.customization.1" />
                                <br />
                                <ul>
                                    <li>
                                        <IntlMessages id="dialog.privacy.customization.1.1" />
                                    </li>
                                    <li>
                                        <IntlMessages id="dialog.privacy.customization.1.2" />
                                    </li>
                                </ul>
                                <IntlMessages id="dialog.privacy.customization.2" />
                            </div>
                            <div className="label-category" />
                        </div>
                    </section>
                </Dialog>
            )}
            {showPolicy && (
                <CookieConsent type={policyTypeToShow} close={() => showPolicyHandler()} showPolicyHandler={showPolicyHandler} />
            )}
        </>
    );
};

export default memo(CustomizeCookiesDialog);
